import { PdfAreaDontBreak, PdfH3 } from 'pages/v2/compiler/PDF/Styles.styled';
import { Column } from 'primereact/column';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEpdScenarioModule, useEpdScenarioUnits } from 'services/api/queries';
import styled, { css } from 'styled-components';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ScenarioModuleItemModel, ScenarioModuleRequest } from 'types/types';

import { DataTableStyled } from '../StyledGrid';
import ScenarioModuleDescriptionPdf from './ScenarioModuleDescription';

type TProps = {
  epdVersionId: string;
  scenarioId: string;
  module: string;
  moduleName: string;
  gridCaption?: string;
  moduleSectionTitle?: string | React.ReactNode;
  isDefaultScenario: boolean;
  isEPDComplete: boolean;
};

const ModuleGridPdf = ({
  epdVersionId,
  scenarioId,
  module,
  moduleName,
  gridCaption,
  moduleSectionTitle,
  isDefaultScenario,
  isEPDComplete,
}: TProps) => {
  const { t } = useTranslation();

  const units = useEpdScenarioUnits().data;
  const scenarioModule = useEpdScenarioModule({ epdVersionId, scenarioId, module } as ScenarioModuleRequest).data;

  const rows = scenarioModule?.scenarioModuleItems;
  const valuableRows = scenarioModule?.scenarioModuleItems?.filter((x) => x.unitId || x.value);
  const hasValuableRows = valuableRows && valuableRows.length > 0;
  const hasDescription = scenarioModule?.defaultScenarioModuleDescription?.trim();
  const isEmptyModule = !hasValuableRows && !hasDescription;
  const showDescriptionBlock = isDefaultScenario || hasDescription || isEmptyModule || module === 'RSL';
  const showGrid = hasValuableRows || isEmptyModule;

  const header = gridCaption ? <HeaderCaptionSemiBold>{gridCaption}</HeaderCaptionSemiBold> : undefined;

  const descriptionBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    return rowData?.mandatory ? (
      <DescriptionCell
        readonly
        dangerouslySetInnerHTML={{
          __html: t(`epdCompiler.step3.scenarioTab.epdScenarioModule.${module}.description.${rowData.placeholder}`),
        }}
      />
    ) : (
      <DescriptionCell readonly={false}>{rowData?.description ?? <>&nbsp;</>}</DescriptionCell>
    );
  };

  const unitBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    const option = units?.find((x) => x.value === rowData.unitId);
    return option && option !== null && <div dangerouslySetInnerHTML={{ __html: option.label }} />;
  };

  return (
    <PdfAreaDontBreak>
      {moduleSectionTitle &&
        (typeof moduleSectionTitle === 'string' ? <PdfH3>{moduleSectionTitle}</PdfH3> : <>{moduleSectionTitle}</>)}

      {showDescriptionBlock && (
        <ScenarioModuleDescriptionPdf
          module={module}
          moduleName={moduleName}
          isDefaultScenario={isDefaultScenario}
          scenarioModule={scenarioModule}
          isEPDComplete={isEPDComplete}
        />
      )}

      {showGrid && (
        <ModuleDataTableStyled value={hasValuableRows ? valuableRows : rows} dataKey="id" header={header} showGridlines>
          <Column
            field="description"
            header={module === 'RSL' ? `${moduleName} information` : `Module ${moduleName} information`}
            body={descriptionBodyTemplate}
            style={{ width: '40%' }}
          />
          <Column field="value" header="Value" className="padding" style={{ width: '40%' }} />
          <Column field="unitId" header="Unit" body={unitBodyTemplate} className="padding" style={{ width: '40%' }} />
        </ModuleDataTableStyled>
      )}
    </PdfAreaDontBreak>
  );
};

const ModuleDataTableStyled = styled(DataTableStyled)`
  border-collapse: collapse;
  .p-datatable-table {
    height: 100%;
  }
  .p-datatable-tbody > tr > td {
    padding: 0;
  }
  .p-datatable-tbody > tr > td.padding,
  .p-datatable-tbody > tr.p-datatable-emptymessage > td {
    padding: 0.5rem 1rem;
  }
  && .p-inputtext.p-dropdown-label.p-placeholder {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
  .p-checkbox.p-component.p-disabled > .p-checkbox-box.p-component {
    opacity: 1;
    border-color: ${(props) => props.theme.colors.elementsDropDown};
  }
`;

const CellContainer = styled.div<{ readonly: boolean }>`
  ${(props) =>
    props.readonly &&
    css`
      background-color: ${(props) => props.theme.colors.elementsFilledInput};
    `}
  padding: 0.5rem 1rem;
  height: 100%;
`;

const DescriptionCell = styled(CellContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export default ModuleGridPdf;
